import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "store/rootReducer";
import { API_URL } from "config";
import { batch } from "react-redux";
import { AccessoriesAdminState } from "./types";
import { setResponseMessage } from "../adminSlice";
import userStorage from "store/UserStorage";
import { ObjectType } from "features/types";
import { transliterate } from "../Products/actions/utils";

const initialState: AccessoriesAdminState = {
  accessories: [],
  accessory: undefined,
  options: undefined,
  loading: false,
};

const accessoriesAdmin = createSlice({
  name: "accessoriesAdminSlice",
  initialState,
  reducers: {
    setAccessories(state, action) {
      state.accessories = action.payload;
    },
    setAccessoryForEdit(state, action) {
      state.accessory = action.payload;
    },
    setAccessoryOptions(state, action) {
      state.options = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
  },
});
export const { setLoading, setAccessories, setAccessoryForEdit, setAccessoryOptions } = accessoriesAdmin.actions;
export const selectLoading = (state: RootState) => state.accessoriesAdmin.loading;
export const selectAccessoryOptions = (state: RootState) => state.accessoriesAdmin.options;
export const selectAllAccessories = (state: RootState) => state.accessoriesAdmin.accessories;
export const selectAccessoryForEditing = (state: RootState) => state.accessoriesAdmin.accessory;
export const fetchAllAccessories = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    const result = await axios.get(`${API_URL}/admin/accessory`);
    batch(() => {
      dispatch(setAccessories(result.data.data));
      dispatch(setResponseMessage({ message: "", hasError: false }));
      dispatch(setLoading(false));
    });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    batch(() => {
      dispatch(setResponseMessage({ message: errorMessage, hasError: true }));
      dispatch(setLoading(false));
    });

    console.log(`Error in fetchAllAccessories: ${errorMessage}`);
  }
};

export const fetchAccessoryOptions = () => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    const result = await axios.get(`${API_URL}/admin/accessory-options`);
    batch(() => {
      dispatch(setAccessoryOptions(result.data.data));
      dispatch(setResponseMessage({ message: "", hasError: false }));
      dispatch(setLoading(false));
    });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    batch(() => {
      dispatch(setResponseMessage({ message: errorMessage, hasError: true }));
      dispatch(setLoading(false));
    });

    console.log(`Error in fetchAccessoryOptions: ${errorMessage}`);
  }
};

export const addAccessory = (data: any, onUploadProgress: (e: ProgressEvent<EventTarget>) => void) => async (
  dispatch: any
) => {
  try {
    const accessory: {
      title: string;
      brand: string;
      price_promotion: number;
      price_promotion_eur?: number;
      price: number;
      price_eur?: number;
      description: string;
      quantity: number;
      objectType: ObjectType;
      alias: string;
      titleMeta: string;
      tagsMeta: string;
      descriptionMeta: string;
    } = {
      title: data.title,
      alias: data.alias ?? transliterate(`${data.brand} ${data.title}`),

      brand: data.brand,
      price: data.price,
      price_eur: data.price_eur,
      price_promotion: data.price_promotion,
      price_promotion_eur: data.price_promotion_eur,
      quantity: data.quantity,
      description: data.description,
      objectType: ObjectType.accessory,
      titleMeta: data.titleMeta,
      tagsMeta: data.tagsMeta,
      descriptionMeta: data.descriptionMeta,
    };
    dispatch(setLoading(true));
    let formData = new FormData();
    formData.append("file", data.image.file);
    formData.append("accessory", JSON.stringify(accessory));

    let token = userStorage.getUser();
    const response = await axios.post(`${API_URL}/admin/accessory`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    dispatch(
      setResponseMessage({
        message: "Успешно качихте аксесоара.",
        hasError: false,
      })
    );
  } catch (error: unknown) {
    batch(() => {
      dispatch(setResponseMessage({ 
        message: error instanceof Error ? error.message : 'An unknown error occurred',
        hasError: true 
      }));
      dispatch(setLoading(false));
    });
  }
};

export const updateAccessory = (data: any, onUploadProgress: (e: ProgressEvent<EventTarget>) => void) => async (
  dispatch: any
) => {
  try {
    dispatch(setLoading(true));
    const accessory: {
      title: string;
      brand: string;
      price_promotion: number;
      price_promotion_eur?: number;
      price: number;
      price_eur?: number;
      description: string;
      quantity: number;
      objectType: ObjectType;
      alias: string;
      titleMeta: string;
      tagsMeta: string;
      descriptionMeta: string;
    } = {
      title: data.data.title,
      alias: data.data.alias ?? transliterate(`${data.data.brand} ${data.data.title}`),
      brand: data.data.brand,
      price: data.data.price,
      price_eur: data.data.price_eur,
      price_promotion: data.data.price_promotion,
      price_promotion_eur: data.data.price_promotion_eur,
      quantity: data.data.quantity,
      description: data.data.description,
      objectType: ObjectType.accessory,
      titleMeta: data.data.titleMeta,
      tagsMeta: data.data.tagsMeta,
      descriptionMeta: data.data.descriptionMeta,
    };
    let formData = new FormData();

    formData.append("accessory", JSON.stringify(accessory));

    formData.append("file", data.data.image);
    let token = userStorage.getUser();
    const response = await axios.put(`${API_URL}/admin/accessory/${data.id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    dispatch(
      setResponseMessage({
        message: "Успешно променихте аксесоара.",
        hasError: false,
      })
    );
  } catch (error: unknown) {
    batch(() => {
      dispatch(setResponseMessage({ 
        message: error instanceof Error ? error.message : 'An unknown error occurred', 
        hasError: true 
      }));
      dispatch(setLoading(false));
    });
  }
};

export const deleteAccessory = (id: string) => async (dispatch: any) => {
  try {
    let token = userStorage.getUser();
    const response = await axios.delete(`${API_URL}/admin/accessory/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно изтрихте аксесоара.",
          hasError: false,
        })
      );
      dispatch(fetchAllAccessories());
    });
  } catch (error: unknown) {
    batch(() => {
      dispatch(setResponseMessage({ 
        message: error instanceof Error ? error.message : 'An unknown error occurred',
        hasError: true 
      }));
      dispatch(setLoading(false));
    });
  }
};

export const fetchAccessory = (id: string) => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    let token = userStorage.getUser();
    const result = await axios.get(`${API_URL}/admin/accessory-values/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    dispatch(setAccessoryForEdit(result.data.data));
  } catch (error: any) {
    batch(() => {
      dispatch(setLoading(false));
      dispatch(setResponseMessage({ 
        message: error instanceof Error ? error.message : 'An unknown error occurred',
        hasError: true 
      }));
    });
  }
};

export default accessoriesAdmin.reducer;
